// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// Answers: what has actually happened to the price of fuel? Twenty-five years
// of daily medians, with regions compared directly.

import { loadRegionSeries, loadSeriesIndex, prefs } from '../data';
import type { RegionSeries, SeriesIndex } from '../types';
import { chartSvg, linePath, mark, svgEl, text, ticks } from '../chart';
import { PRODUCT_NAMES, categoryColour, cents, esc, escTip, regionSlug, shortDate } from '../format';
import { glossaryTerm } from '../glossary';

const MAX_SERIES = 6;

export async function renderHistory(root: HTMLElement): Promise<void> {
  root.innerHTML = '<div class="skeleton"></div>';
  const index = await loadSeriesIndex();

  const allRegions: Array<{ slug: string; state: string; region: string }> = [];
  for (const [state, info] of Object.entries(index.states)) {
    for (const region of info.regions) allRegions.push({ slug: regionSlug(state, region), state, region });
  }
  const defaults = ['WA-metro', 'QLD-brisbane-inner-city'].filter((s) => allRegions.some((r) => r.slug === s));
  let selected = prefs.get('historyRegions', defaults.join(',')).split(',').filter((s) => allRegions.some((r) => r.slug === s));
  if (!selected.length) selected = defaults.length ? defaults : [allRegions[0].slug];
  let product = prefs.get('historyProduct', 'ULP');

  root.innerHTML = `
    <div class="view-head">
      <h1>Twenty-five years of the pump price</h1>
      <p>The daily ${glossaryTerm('median')} price across every reporting station, from 3 January 2001.
      Western Australia publishes a full ${glossaryTerm('snapshot', 'daily snapshot')}; Queensland's
      ${glossaryTerm('changeLog', 'change log')} has been carried forward into the same daily shape.</p>
    </div>
    <div class="panel">
      <div class="controls">
        <label for="hist-product">Fuel</label>
        <select id="hist-product">
          ${['ULP', 'DSL', 'P95', 'P98', 'E10', 'LPG'].map((p) => `<option value="${esc(p)}"${p === product ? ' selected' : ''}>${esc(PRODUCT_NAMES[p] ?? p)}</option>`).join('')}
        </select>
        <label for="hist-add">Add a region</label>
        <select id="hist-add">
          <option value="">Choose…</option>
          ${['WA', 'QLD'].map((st) => `<optgroup label="${st === 'WA' ? 'Western Australia' : 'Queensland'}">${
            allRegions.filter((r) => r.state === st).map((r) => `<option value="${esc(r.slug)}">${esc(r.region)}</option>`).join('')
          }</optgroup>`).join('')}
        </select>
        <span class="brand-sub">Up to ${MAX_SERIES} regions</span>
      </div>
      <div id="hist-chips" class="legend" style="margin-bottom:var(--space-md)"></div>
      <div class="chart-scroll"><div class="chart-frame" id="hist-frame"></div></div>
      <p class="brand-sub" style="margin-top:var(--space-sm)" id="hist-note"></p>
    </div>
  `;

  const productSel = root.querySelector('#hist-product') as HTMLSelectElement;
  const addSel = root.querySelector('#hist-add') as HTMLSelectElement;
  productSel.addEventListener('change', () => { product = productSel.value; prefs.set('historyProduct', product); void draw(); });
  addSel.addEventListener('change', () => {
    const slug = addSel.value;
    addSel.value = '';
    if (!slug || selected.includes(slug)) return;
    selected = [...selected, slug].slice(-MAX_SERIES);
    prefs.set('historyRegions', selected.join(','));
    void draw();
  });

  async function draw(): Promise<void> {
    const frame = root.querySelector('#hist-frame') as HTMLElement;
    frame.innerHTML = '<div class="skeleton" style="height:360px"></div>';
    const loaded = await Promise.all(selected.map((slug) => loadRegionSeries(slug).catch(() => null)));
    const usable = loaded.filter((s): s is RegionSeries => !!s && !!s.series[product]);
    renderChips(root.querySelector('#hist-chips') as HTMLElement, selected, allRegions, (slug) => {
      selected = selected.filter((s) => s !== slug);
      if (!selected.length) selected = [allRegions[0].slug];
      prefs.set('historyRegions', selected.join(','));
      void draw();
    });
    frame.innerHTML = '';
    if (!usable.length) {
      frame.innerHTML = `<p class="empty">No ${esc(PRODUCT_NAMES[product] ?? product)} series for the selected regions. Try another fuel.</p>`;
      return;
    }
    frame.appendChild(buildChart(usable, index, product));
    const spans = usable.map((s) => {
      const dates = index.states[s.state].dates;
      const med = s.series[product].med;
      let first = -1;
      let last = -1;
      for (let i = 0; i < med.length; i++) {
        if (med[i] == null) continue;
        if (first < 0) first = i;
        last = i;
      }
      return `${s.region}: ${shortDate(dates[first])} → ${shortDate(dates[last])}`;
    });
    root.querySelector('#hist-note')!.textContent = `${spans.join(' · ')}. Prices are nominal — not adjusted for inflation.`;
  }
  await draw();
}

function renderChips(
  host: HTMLElement,
  selected: string[],
  all: Array<{ slug: string; state: string; region: string }>,
  onRemove: (slug: string) => void,
): void {
  host.innerHTML = '';
  for (const slug of selected) {
    const meta = all.find((r) => r.slug === slug);
    if (!meta) continue;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'key';
    btn.setAttribute('aria-pressed', 'true');
    btn.title = `Remove ${meta.region}`;
    btn.innerHTML = `<span class="swatch" style="background:${categoryColour(slug)}"></span>${esc(meta.region)} <span aria-hidden="true">✕</span>`;
    btn.addEventListener('click', () => onRemove(slug));
    host.appendChild(btn);
  }
}

function buildChart(series: RegionSeries[], index: SeriesIndex, product: string): SVGSVGElement {
  const width = 1080;
  const height = 420;
  const padL = 52; const padR = 16; const padT = 16; const padB = 46;
  const innerW = width - padL - padR;
  const innerH = height - padT - padB;

  let minDate = '9999';
  let maxDate = '0000';
  let lo = Infinity;
  let hi = -Infinity;
  for (const s of series) {
    const dates = index.states[s.state].dates;
    const med = s.series[product].med;
    for (let i = 0; i < med.length; i++) {
      const v = med[i];
      if (v == null) continue;
      if (dates[i] < minDate) minDate = dates[i];
      if (dates[i] > maxDate) maxDate = dates[i];
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
  }
  const t0 = Date.parse(`${minDate}T00:00:00Z`);
  const t1 = Date.parse(`${maxDate}T00:00:00Z`);
  const pad = (hi - lo) * 0.06;
  lo -= pad; hi += pad;

  const xFor = (iso: string) => padL + ((Date.parse(`${iso}T00:00:00Z`) - t0) / Math.max(1, t1 - t0)) * innerW;
  const yFor = (v: number) => padT + innerH - ((v - lo) / Math.max(1, hi - lo)) * innerH;

  const svg = chartSvg(width, height, `Daily ${PRODUCT_NAMES[product] ?? product} price by region`);
  for (const t of ticks(lo, hi, 7)) {
    svg.appendChild(svgEl('line', { x1: padL, x2: padL + innerW, y1: yFor(t), y2: yFor(t), class: 'gridline' }));
    svg.appendChild(text(padL - 8, yFor(t) + 4, (t / 10).toFixed(0), { 'text-anchor': 'end', 'font-size': 12 }));
  }
  const y0 = Number(minDate.slice(0, 4));
  const y1 = Number(maxDate.slice(0, 4));
  const yearStep = y1 - y0 > 18 ? 3 : y1 - y0 > 8 ? 2 : 1;
  for (let y = Math.ceil(y0 / yearStep) * yearStep; y <= y1; y += yearStep) {
    const x = xFor(`${y}-01-01`);
    if (x < padL || x > padL + innerW) continue;
    svg.appendChild(svgEl('line', { x1: x, x2: x, y1: padT, y2: padT + innerH, class: 'gridline' }));
    svg.appendChild(text(x, height - 24, String(y), { 'text-anchor': 'middle', 'font-size': 12 }));
  }
  svg.appendChild(text(padL - 8, padT - 2, 'c/L', { 'text-anchor': 'end', 'font-size': 11, fill: '#857f78' }));

  for (const s of series) {
    const slug = regionSlug(s.state, s.region);
    const colour = categoryColour(slug);
    const dates = index.states[s.state].dates;
    const med = s.series[product].med;
    const pts: Array<[number, number] | null> = [];
    // One point per rendered pixel column at most — a 9,351-point path drawn at
    // 1,080px wide is 8× more geometry than the screen can show.
    const stride = Math.max(1, Math.floor(med.length / innerW));
    for (let i = 0; i < med.length; i += stride) {
      let v: number | null = null;
      for (let j = i; j < Math.min(med.length, i + stride); j++) if (med[j] != null) { v = med[j]; break; }
      pts.push(v == null ? null : [xFor(dates[i]), yFor(v)]);
    }
    svg.appendChild(svgEl('path', { d: linePath(pts), fill: 'none', stroke: colour, 'stroke-width': 1.5, 'stroke-linejoin': 'round' }));

    // Hover targets: one invisible wide band per ~24px so every part of every
    // line has a tooltip, without 9,000 hit areas.
    const band = Math.max(1, Math.floor(med.length / (innerW / 24)));
    for (let i = 0; i < med.length; i += band) {
      const v = med[i];
      if (v == null) continue;
      const dot = svgEl('circle', { cx: xFor(dates[i]), cy: yFor(v), r: 8, fill: 'transparent' });
      mark(dot, `${s.region} (${s.state})\n${shortDate(dates[i])}\n${cents(v)}c/L`);
      svg.appendChild(dot);
      const visible = svgEl('circle', { cx: xFor(dates[i]), cy: yFor(v), r: 1.6, fill: colour, opacity: 0.55 });
      svg.appendChild(visible);
    }
  }
  void escTip;
  return svg;
}
